import React, { useMemo, useState } from 'react';
import {
  PaginatedResult,
  platformCoreNetworkApi,
  PlatformMember,
  PlatformPortalToken,
  PlatformSettings,
} from '../../../api/platform/coreNetwork';
import { buildTalentLoginUrl, buildTalentPortalUrl } from '../../../config/environment';
import { formatDateTime, formatEmptyValue } from '../utils/format';
import { isVerificationApproved } from '../config/verification';
import { VerificationStatusBadge } from './TalentVerificationCard';
import { ConfirmDialog, FeedbackBanner, InlineActions, KeyValueGrid, MvpCard, StatusBadge } from './MvpTalentUi';

interface PortalAccessCardProps {
  member: PlatformMember;
  portalSessions: PaginatedResult<PlatformPortalToken>;
  settings: PlatformSettings;
  onRefresh: () => Promise<void> | void;
  onFeedback: (feedback: { tone: 'success' | 'error'; message: string }) => void;
  verificationActions?: React.ReactNode;
  verificationBusy?: boolean;
}

type PortalAction = 'enable' | 'resend' | 'restore' | 'unlock' | 'revoke' | 'revokeSessions';

const actionConfig: Record<PortalAction, { title: string; label: string; tone?: 'default' | 'danger'; sendInvitation?: boolean }> = {
  enable: { title: 'Enable Work Portal access', label: 'Enable Work Portal', sendInvitation: true },
  resend: { title: 'Resend Work Portal invitation', label: 'Resend invitation', sendInvitation: true },
  restore: { title: 'Restore Work Portal access', label: 'Restore portal access', sendInvitation: true },
  unlock: { title: 'Unlock Work Portal access', label: 'Unlock portal access' },
  revoke: { title: 'Revoke Work Portal access', label: 'Revoke portal access', tone: 'danger' },
  revokeSessions: { title: 'Revoke all Work Portal sessions', label: 'Revoke all sessions', tone: 'danger' },
};

const latestInvitation = (sessions: PlatformPortalToken[]) => sessions.find((token) => token.tokenType === 'invitation') || sessions[0];

const getEnableDisabledReason = (member: PlatformMember, settings: PlatformSettings) => {
  if (!member.email) return 'A talent email address is required before an invitation can be sent.';
  if (!isVerificationApproved(member.verificationStatus)) {
    return 'Approve or verify this talent before enabling Work Portal access.';
  }
  if (settings.portalAccessMode === 'disabled') return 'Global portal access is disabled in Platform Settings.';
  return null;
};

const PortalAccessCard: React.FC<PortalAccessCardProps> = ({ member, portalSessions, settings, onRefresh, onFeedback, verificationActions, verificationBusy = false }) => {
  const [dialogAction, setDialogAction] = useState<PortalAction | null>(null);
  const [busyAction, setBusyAction] = useState<PortalAction | null>(null);
  const portalStatus = String(member.portalStatus || 'not_enabled').toLowerCase();
  const portalLinks = useMemo(() => {
    try {
      return {
        dashboardUrl: buildTalentPortalUrl(),
        error: null as string | null,
        loginUrl: buildTalentLoginUrl(),
      };
    } catch (error: any) {
      return {
        dashboardUrl: null,
        error: error?.message || 'Talent Portal URL is not configured for this environment.',
        loginUrl: null,
      };
    }
  }, []);
  const latestToken = latestInvitation(portalSessions.rows);
  const enableDisabledReason = getEnableDisabledReason(member, settings);
  const verificationApproved = isVerificationApproved(member.verificationStatus);
  const isBusy = Boolean(busyAction) || verificationBusy;

  const runAction = async (action: PortalAction, reason: string) => {
    setBusyAction(action);
    try {
      if (action === 'enable') {
        const result = await platformCoreNetworkApi.enableMemberPortalAccess(member.id, { sendInvitation: true, reason });
        onFeedback({ tone: result.warning ? 'error' : 'success', message: result.warning || 'Work Portal invitation created and queued for delivery.' });
      } else if (action === 'resend') {
        const result = await platformCoreNetworkApi.resendMemberPortalInvitation(member.id, { reason });
        onFeedback({ tone: result.warning ? 'error' : 'success', message: result.warning || 'Work Portal invitation resent.' });
      } else if (action === 'restore') {
        const result = await platformCoreNetworkApi.restoreMemberPortalAccess(member.id, { reason });
        onFeedback({ tone: result.warning ? 'error' : 'success', message: result.warning || 'Work Portal access restored.' });
      } else if (action === 'unlock') {
        await platformCoreNetworkApi.unlockMemberPortalAccess(member.id, reason);
        onFeedback({ tone: 'success', message: 'Work Portal access unlocked.' });
      } else if (action === 'revokeSessions') {
        const result = await platformCoreNetworkApi.revokeAllMemberSessions(member.id, reason);
        onFeedback({ tone: 'success', message: `${result.revokedSessionCount} Work Portal session${result.revokedSessionCount === 1 ? '' : 's'} revoked.` });
      } else {
        await platformCoreNetworkApi.revokePortalAccess(member.id, reason);
        onFeedback({ tone: 'success', message: 'Work Portal access revoked.' });
      }
      await onRefresh();
    } catch (error: any) {
      onFeedback({ tone: 'error', message: error?.message || 'Unable to update Work Portal access.' });
      throw error;
    } finally {
      setBusyAction(null);
    }
  };

  const renderPortalActions = () => {
    const openButton = (
      portalLinks.dashboardUrl ? (
        <a className="mvp-button mvp-button--ghost" href={portalLinks.dashboardUrl} target="_blank" rel="noopener noreferrer">
          Open Talent Portal
        </a>
      ) : null
    );

    if (portalStatus === 'not_enabled') {
      return (
        <button type="button" className="mvp-button" onClick={() => setDialogAction('enable')} disabled={isBusy || Boolean(enableDisabledReason)} title={enableDisabledReason || undefined}>
          Enable Work Portal
        </button>
      );
    }
    if (portalStatus === 'invited') {
      return <><button type="button" className="mvp-button" onClick={() => setDialogAction('resend')} disabled={isBusy}>Resend invitation</button><button type="button" className="mvp-button mvp-button--danger" onClick={() => setDialogAction('revoke')} disabled={isBusy}>Revoke portal access</button>{openButton}</>;
    }
    if (portalStatus === 'active') {
      return <>{openButton}<button type="button" className="mvp-button mvp-button--ghost" onClick={() => setDialogAction('revokeSessions')} disabled={isBusy}>Revoke all sessions</button><button type="button" className="mvp-button mvp-button--danger" onClick={() => setDialogAction('revoke')} disabled={isBusy}>Revoke portal access</button></>;
    }
    if (portalStatus === 'locked') {
      return <><button type="button" className="mvp-button" onClick={() => setDialogAction('unlock')} disabled={isBusy}>Unlock portal access</button><button type="button" className="mvp-button mvp-button--ghost" onClick={() => setDialogAction('revokeSessions')} disabled={isBusy}>Revoke all sessions</button><button type="button" className="mvp-button mvp-button--danger" onClick={() => setDialogAction('revoke')} disabled={isBusy}>Revoke portal access</button></>;
    }
    if (portalStatus === 'revoked') {
      return <button type="button" className="mvp-button" onClick={() => setDialogAction('restore')} disabled={isBusy || Boolean(enableDisabledReason)} title={enableDisabledReason || undefined}>Restore portal access</button>;
    }
    return openButton;
  };

  const invitationItems = [
    { label: 'Destination email', value: formatEmptyValue(latestToken?.sentToEmail || member.email || '') },
    { label: 'Sent date', value: formatDateTime(latestToken?.createdAt) },
    { label: 'Expiry date', value: formatDateTime(latestToken?.expiresAt) },
    { label: 'Latest delivery status', value: formatEmptyValue(latestToken?.metadataJson?.deliveryStatus as string) },
  ];

  const activeItems = [
    { label: 'Verified date', value: formatDateTime(member.portalVerifiedAt) },
    { label: 'Last login', value: formatDateTime(member.portalLastLoginAt) },
    { label: 'Locked until', value: formatDateTime(member.portalLockedUntil) },
    { label: 'Session count', value: portalSessions.pagination.total },
  ];

  const config = dialogAction ? actionConfig[dialogAction] : null;

  return (
    <MvpCard title="Work Portal Access">
      <div className="mvp-portal-access">
        <div className="mvp-portal-access__header">
          <div>
            <p className="mvp-portal-access__eyebrow">Talent-facing portal lifecycle</p>
            <h4>{portalStatus === 'not_enabled' ? 'Work Portal access has not been enabled for this talent.' : portalStatus === 'invited' ? 'Invitation sent' : 'Current portal access'}</h4>
            <p>Talent approval and Work Portal login access are separate states. Approval reviews eligibility; portal enablement creates a secure invitation for the talent-facing Work Portal.</p>
          </div>
          <StatusBadge status={member.portalStatus || 'not_enabled'} />
        </div>

        {settings.portalAccessMode === 'disabled' ? <FeedbackBanner tone="error" message="Global portal access is disabled. Update Platform Settings before creating invitations." /> : null}
        {portalLinks.error ? <FeedbackBanner tone="error" message="Talent Portal URL is not configured for this environment." /> : null}
        {settings.portalAccessMode === 'read_only' ? <div className="mvp-portal-access__notice">Global portal mode is read-only: talent can sign in and view work, but talent-facing submissions or work changes may be restricted. Administrators can still manage verification and access.</div> : null}
        {verificationApproved && (portalStatus === 'not_enabled' || portalStatus === 'revoked') && settings.portalAccessMode !== 'disabled' ? <div className="mvp-portal-access__notice">This talent is approved and eligible for Work Portal access.</div> : null}
        {enableDisabledReason && (portalStatus === 'not_enabled' || portalStatus === 'revoked') ? (
          <div className="mvp-portal-access__notice">
            <p>{verificationBusy ? 'Approving talent...' : enableDisabledReason}</p>
            {!verificationApproved ? verificationActions : null}
          </div>
        ) : null}

        <KeyValueGrid items={[
          { label: 'Talent email', value: formatEmptyValue(member.email) },
          { label: 'Verification status', value: <VerificationStatusBadge status={member.verificationStatus} /> },
          { label: 'Global portal mode', value: <StatusBadge status={settings.portalAccessMode} /> },
          { label: 'Talent Portal login', value: portalLinks.loginUrl || 'Talent Portal URL is not configured for this environment.' },
        ]} />

        {portalStatus === 'invited' ? <KeyValueGrid items={invitationItems} /> : null}
        {portalStatus === 'active' || portalStatus === 'locked' ? <KeyValueGrid items={activeItems} /> : null}

        <InlineActions>{renderPortalActions()}</InlineActions>
      </div>

      <ConfirmDialog
        open={Boolean(dialogAction && config)}
        title={config?.title || ''}
        description={`${member.displayName} (${member.email || 'no email'}) is ${member.verificationStatus || 'unverified'} with portal status ${member.portalStatus || 'not_enabled'}. ${config?.sendInvitation ? 'A secure one-time invitation will be sent to the talent email.' : 'Provide an administrative reason for the audit log.'}`}
        confirmLabel={config?.label || 'Confirm'}
        confirmTone={config?.tone || 'default'}
        onClose={() => setDialogAction(null)}
        onConfirm={async ({ reason }) => {
          if (!dialogAction) return;
          await runAction(dialogAction, reason);
        }}
      />
    </MvpCard>
  );
};

export default PortalAccessCard;
